import { create } from 'zustand';
import { useReactFlow } from 'reactflow';
import { createReactFlowData } from 'components/CreateSpark/SparkBuilderCanvas/hooks';
import type { SparkStep } from 'types/SparkStep';
import type {
  SparkBuilderStateType,
  SparkBuilderSliceType,
} from 'types/store/sparkBuilder';
import type { StateCreator } from 'zustand';
import { generateMap } from 'utilities/hooks/responseHandlers';

const NULL_INITIAL_STORE_VALUES: SparkBuilderStateType = {
  sparkSteps: {
    data: undefined,
    previousData: undefined,
    isLoading: null,
    isFetching: null,
  },
  stepTypeIdMap: null,
  isReferenceBreak: false,
  joinedSpark: undefined,
  sparkID: '',
  builderSideBar: {
    selectedNavListItemID: null,
    selectedStep: null,
  },
  canvas: {
    stepOrder: null,
    isAddDialogOpen: null,
  },
};

const sparkBuilderSlice: StateCreator<SparkBuilderSliceType> = (
  set,
  get,
  store
) => ({
  ...NULL_INITIAL_STORE_VALUES,
  setJoinedSpark: (joinedSpark) => {
    set((state) => ({
      ...state,
      joinedSpark,
      sparkID: joinedSpark.id,
    }));
  },
  resetStore: () =>
    set((state) => ({
      ...state,
      ...NULL_INITIAL_STORE_VALUES,
    })),
  sparkStepsActions: {
    setSparkSteps: (steps) =>
      set((state) => ({
        ...state,
        sparkSteps: {
          ...state.sparkSteps,
          data: steps,
          previousData: steps,
        },
      })),

    updateSparkStepAttributes: (sparkStepAttribute, stepID) =>
      set((state) => {
        const steps = [...(state.sparkSteps.data || [])];
        const index = steps.findIndex((step) => step.id === stepID);
        if (index !== -1) {
          steps[index] = {
            ...steps[index],
            attributes: { ...steps[index].attributes, ...sparkStepAttribute },
          };
        }
        return {
          ...state,
          sparkSteps: {
            ...state.sparkSteps,
            data: steps,
          },
        };
      }),

    updateSparkStep: (step, stepID) => {
      const id = stepID || step.id;
      set((state) => {
        const steps = [...(state.sparkSteps.data || [])];
        const index = steps.findIndex((step) => step.id === id);
        if (index !== -1) {
          steps[index] = { ...steps[index], ...step };
        }
        return {
          ...state,
          sparkSteps: {
            ...state.sparkSteps,
            data: steps,
          },
        };
      });
    },

    getSparkStep: (stepID) => {
      const steps = get().sparkSteps.data;
      const foundStep = steps?.find((step) => step?.id === stepID);
      return foundStep;
    },

    setSparkStepState: (isLoading, isFetching) =>
      set((state) => ({
        ...state,
        sparkSteps: {
          ...state.sparkSteps,
          isFetching: !!isFetching,
          isLoading: !!isLoading,
        },
      })),

    restorePreviousSparkBuilderState: () =>
      set((state) => ({
        ...state,
        sparkSteps: {
          ...state.sparkSteps,
          data: state.sparkSteps.previousData,
        },
      })),

    deleteSparkStep: (sparkStepID) =>
      set((state) => ({
        sparkSteps: {
          ...state.sparkSteps,
          data: state.sparkSteps.data?.filter(
            (stepData) => stepData.id !== sparkStepID
          ),
        },
      })),

    addSparkStep: (sparkStep) =>
      set((state) => {
        const steps = [...(state.sparkSteps.data || [])];
        const newSparkStepIndex = sparkStep.attributes.stepOrder;
        steps.splice(newSparkStepIndex - 1, 0, sparkStep);
        return {
          ...state,
          sparkSteps: { ...state.sparkSteps, data: steps },
        };
      }),

    updatePreviousState: () =>
      set((state) => ({
        ...state,
        sparkSteps: {
          ...state.sparkSteps,
          previousData: state.sparkSteps.data,
        },
      })),

    reArrangeSparkSteps: (sparkStepOrder) =>
      set((state) => {
        const steps = get().sparkSteps.data;

        const orderIDMap = generateMap(sparkStepOrder, 'sparkStepID');

        const newOrderSteps = steps?.map((step) => {
          const newOrder = orderIDMap?.[step.id].stepOrder;
          const reOrderedStep = {
            ...step,
            attributes: {
              ...step.attributes,
              stepOrder: newOrder,
            },
          };
          return reOrderedStep as SparkStep;
        });

        return {
          ...state,
          sparkSteps: {
            ...state.sparkSteps,
            data: newOrderSteps,
          },
        };
      }),

    updateSortOrder: () =>
      set((state) => {
        const steps = [...(state.sparkSteps.data || [])];
        const updatedSteps = steps.map((step, index) => ({
          ...step,
          attributes: { ...step.attributes, stepOrder: index + 1 },
        }));
        return {
          ...state,
          sparkSteps: { ...state.sparkSteps, data: updatedSteps },
        };
      }),
    setSparkStepTypeIdMap: (idMap) =>
      set((state) => ({
        ...state,
        stepTypeIdMap: idMap,
      })),

    updateStepAndCheckReference: (step, stepId) => {
      const id = stepId || step.id;
      set((state) => {
        const steps = [...(state.sparkSteps.data || [])];
        const index = steps.findIndex((step) => step.id === id);
        if (index === -1) {
          return {
            ...state,
          };
        }

        const previousStepReference =
          steps[index].meta.diagnostics.hasReferenceIssues;
        const currentStepReference = step.meta.diagnostics.hasReferenceIssues;
        const isRefDiff = previousStepReference !== currentStepReference;

        steps[index] = {
          ...steps[index],
          ...step,
          meta: {
            ...step.meta,
            resolvedValues: steps[index].meta.resolvedValues,
          },
        };
        const isBreaking = isRefDiff && currentStepReference;

        return {
          ...state,
          sparkSteps: {
            ...state.sparkSteps,
            data: steps,
          },
          ...(isBreaking && { isReferenceBreak: isBreaking }),
        };
      });
      return get().isReferenceBreak;
    },
    setIsReferenceBreak: (status) => {
      set((state) => ({ ...state, isReferenceBreak: status }));
    },
  },
  builderSideBarActions: {
    setSelectedNavListItemID: (ID) =>
      set((state) => ({
        ...state,
        builderSideBar: {
          ...state.builderSideBar,
          selectedStep: null,
          selectedNavListItemID: ID,
        },
      })),
    setSelectedStep: (stepType) =>
      set((state) => ({
        ...state,
        builderSideBar: {
          ...state.builderSideBar,
          selectedNavListItemID: null,
          selectedStep: stepType,
        },
      })),
  },
  canvasActions: {
    setStepOrder: (order) =>
      set((state) => ({
        ...state,
        canvas: { ...state.canvas, stepOrder: order },
      })),
    openAddDialog: () =>
      set((state) => ({
        ...state,
        canvas: { ...state.canvas, isAddDialogOpen: true },
      })),
    closeAddDialog: () =>
      set((state) => ({
        ...state,
        canvas: { ...state.canvas, isAddDialogOpen: false },
      })),

    useReactFlowElements: () => {
      const { setEdges, setNodes } = useReactFlow();
      const updateReactFlowElements = () => {
        const sparkID = get().joinedSpark?.id;
        const steps = get().sparkSteps.data;
        const chartData = createReactFlowData({
          steps,
          sparkID,
          //updating sort order will in adding new step at the last
          updateStepOrder: true,
        });
        setNodes(chartData?.nodes || []);
        setEdges(chartData?.edges || []);
      };
      return updateReactFlowElements;
    },
  },
});

export const useSparkBuilderStore = create<SparkBuilderSliceType>(
  (set, get, store) => ({ ...sparkBuilderSlice(set, get, store) })
);
